import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "monaco-ide-container" }
const _hoisted_2 = { class: "ide-toolbar" }
const _hoisted_3 = { class: "snippets-dropdown" }
const _hoisted_4 = {
  key: 0,
  class: "snippets-menu"
}
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "main-content" }
const _hoisted_7 = { class: "sidebar" }
const _hoisted_8 = { class: "file-tree" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = {
  key: 1,
  style: {"color":"red"}
}

import { ref, onMounted, onBeforeUnmount, shallowRef, watch } from 'vue'
import * as monaco from 'monaco-editor'
import JSZip from 'jszip'
import { onClickOutside } from '@vueuse/core'

import 'monaco-editor/esm/vs/editor/editor.worker';
import 'monaco-editor/esm/vs/language/json/json.worker';
import 'monaco-editor/esm/vs/language/typescript/ts.worker';

const defaultHtmlContent = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>My Add-In</title>
</head>
<body>
  <body>
  <script>
    Spheeres.onReady(() =>{
      
    });
  <\/script>
  </body>
</body>
</html>`;


export default /*@__PURE__*/_defineComponent({
  __name: 'MonacoIde',
  props: {
  projectBase64: {
    type: String,
    default: ''
  }
},
  emits: ["projectSaved"],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

const FileNode = {    
  name: 'FileNode',
  props: ['nodes'],
  emits: ['open-file'],
  template: `
    <ul style="padding-left: 15px; list-style-type: none;">
      <li v-for="(node, name) in nodes">
        <div v-if="node._isFile" class="file" @click="$emit('open-file', node)">
          📄 {{ name }}
        </div>
        <div v-else class="folder">
          📁 {{ name }}
          <FileNode :nodes="node" @open-file="$emit('open-file', $event)"></FileNode>
        </div>
      </li>
    </ul>
  `
}

const base64Zip = ref(props.projectBase64)
const fileTree = ref({})
const isLoading = ref(false)
const error = ref('')
const editorContainer = ref<HTMLElement | null>(null)

let editor: monaco.editor.IStandaloneCodeEditor | null = null
const fileCache = shallowRef<{ [key: string]: string }>({})

const getLanguageFromPath = (path: string): string => {
  const extension = path.split('.').pop()?.toLowerCase() ?? ''
  const langMap: { [key: string]: string } = {
    js: 'javascript',
    ts: 'typescript',
    json: 'json',
    html: 'html',
    css: 'css',
    md: 'markdown',
    py: 'python',
    java: 'java',
    xml: 'xml',
    php: 'php',
    rb: 'ruby',
    go: 'go'
  }
  return langMap[extension] || 'plaintext'
}

const findFirstFile = (node: any): { path: string; contentPromise: Promise<string> } | null => {
  for (const name in node) {
    const child = node[name]
    if (child._isFile) {
      return child
    }
    if (typeof child === 'object' && !child._isFile) {
      const firstFile = findFirstFile(child)
      if (firstFile) return firstFile
    }
  }
  return null
}

const buildFileTree = (zip: JSZip) => {
  const root: any = {}
  zip.forEach((relativePath, zipEntry) => {
    if (zipEntry.dir) return

    const parts = relativePath.split('/').filter(p => p)
    let currentNode = root

    parts.forEach((part, index) => {
      const isFile = index === parts.length - 1
      if (!currentNode[part]) {
        currentNode[part] = isFile
          ? { _isFile: true, path: relativePath, contentPromise: zipEntry.async('string') }
          : {}
      }
      currentNode = currentNode[part]
    })
  })
  return root
}

const saveProject = async () => {
  if (!Object.keys(fileTree.value).length) {
    alert('No project is loaded.');
    return;
  }

  const zip = new JSZip();

  const models = monaco.editor.getModels();
  const modifiedContent = new Map<string, string>();
  models.forEach((model: monaco.editor.ITextModel) => {
    const modelPath = model.uri.path.substring(1);
    modifiedContent.set(modelPath, model.getValue());
  });

  const addFilesToZip = async (node: any, currentZipFolder: JSZip) => {
    for (const name in node) {
      const child = node[name];
      if (child._isFile) {
        const content = modifiedContent.get(child.path) ?? (await child.contentPromise);
        currentZipFolder.file(name, content);
      } else {
        addFilesToZip(child, currentZipFolder.folder(name));
      }
    }
  };

  await addFilesToZip(fileTree.value, zip);

  emit('projectSaved', await zip.generateAsync({ type: 'base64' }));
};

const loadZip = async () => {
  const dataToLoad = base64Zip.value.trim()
  if (!dataToLoad) {
    fileTree.value = {
      'index.html': {
        _isFile: true,
        path: 'index.html',
        contentPromise: Promise.resolve(defaultHtmlContent)
      }
    };
    const firstFile = findFirstFile(fileTree.value);
    if (firstFile) {
      openFileInEditor(firstFile);
    }
    return;
  }

  isLoading.value = true
  error.value = ''
  fileTree.value = {}
  fileCache.value = {}

  try {
    const zip = await JSZip.loadAsync(dataToLoad, { base64: true })
    fileTree.value = buildFileTree(zip)
    const firstFile = findFirstFile(fileTree.value)
    if (firstFile) {
      openFileInEditor(firstFile)
    }
  } catch (e: any) {
    console.error('Failed to load or unzip the file:', e)
    error.value = `Error: ${e.message}`
    alert('Failed to process the zip file. Make sure it is a valid base64 encoded zip.')
  } finally {
    isLoading.value = false
  }
}

watch(() => props.projectBase64, (newValue) => {
  if (newValue && newValue !== base64Zip.value) {
    base64Zip.value = newValue
    loadZip()
  }
}, { immediate: true })

const openFileInEditor = async (fileNode: { path: string; contentPromise: Promise<string> }) => {
  if (!editor) return

  const filePath = fileNode.path

  let content = fileCache.value[filePath]
  if (!content) {
    content = await fileNode.contentPromise
    fileCache.value = { ...fileCache.value, [filePath]: content }
  }

  const language = getLanguageFromPath(filePath)
  const modelUri = monaco.Uri.parse(`file:///${filePath}`)
  let model = monaco.editor.getModel(modelUri)

  if (!model) {
    model = monaco.editor.createModel(content, language, modelUri)
  } else {
    if (model.getValue() !== content) {
      model.setValue(content)
    }
  }

  editor.setModel(model)
}

const showSnippets = ref(false);
const snippets = ref([
  {
    name: 'Spheeres.onReady',
    code: `Spheeres.onReady(async () => {\n  \n});`
  },
  {
    name: 'Spheeres.createRecord',
    code: `await Spheeres.createRecord({ CONTACTS: { NAME: 'John Doe' } });`
  },
  {
    name: 'Spheeres.createLink',
    code: `await Spheeres.createLink({CONTACTS: 1, COMPANIES: 2});`
  },
  {
    name: 'Spheeres.getDataFromQuery',
    code: `await Spheeres.getDataFromQuery("Query", 5);`
  }
]);
const insertSnippet = (code: string) => {
  if (!editor) return;
  
  const selection = editor.getSelection();
  if (!selection) return;
  editor.executeEdits('snippet', [{ range: selection, text: code, forceMoveMarkers: true }]);
  showSnippets.value = false;
};

onClickOutside(editorContainer, () => {
  showSnippets.value = false;
});

onMounted(async () => {  
  if (editorContainer.value && monaco) {
    editor = monaco.editor.create(editorContainer.value, {
      value: '',
      language: 'javascript',
      theme: 'vs-dark',
      automaticLayout: true
    })
    loadZip();
  } else {
    console.error('Monaco Editor is not available. Please ensure it is loaded.')
  }
})

onBeforeUnmount(() => {
  if (editor) {
    editor.dispose()
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("button", {
        title: "Save Project",
        onClick: saveProject
      }, [...(_cache[1] || (_cache[1] = [
        _createElementVNode("i", { class: "fa-solid fa-floppy-disk" }, null, -1 /* CACHED */),
        _createElementVNode("span", null, "Save", -1 /* CACHED */)
      ]))]),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (showSnippets.value = !showSnippets.value))
        }, [...(_cache[2] || (_cache[2] = [
          _createElementVNode("i", { class: "fa-solid fa-code" }, null, -1 /* CACHED */),
          _createElementVNode("span", null, "Snippets", -1 /* CACHED */)
        ]))]),
        (showSnippets.value)
          ? (_openBlock(), _createElementBlock("ul", _hoisted_4, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(snippets.value, (snippet) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: snippet.name,
                  class: "snippet-item",
                  onClick: ($event: any) => (insertSnippet(snippet.code))
                }, _toDisplayString(snippet.name), 9 /* TEXT, PROPS */, _hoisted_5))
              }), 128 /* KEYED_FRAGMENT */))
            ]))
          : _createCommentVNode("v-if", true)
      ])
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          (isLoading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, "Loading..."))
            : _createCommentVNode("v-if", true),
          (error.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(error.value), 1 /* TEXT */))
            : _createCommentVNode("v-if", true),
          (Object.keys(fileTree.value).length)
            ? (_openBlock(), _createBlock(FileNode, {
                key: 2,
                nodes: fileTree.value,
                onOpenFile: openFileInEditor
              }, null, 8 /* PROPS */, ["nodes"]))
            : _createCommentVNode("v-if", true)
        ])
      ]),
      _createElementVNode("div", {
        ref_key: "editorContainer",
        ref: editorContainer,
        class: "editor-container"
      }, null, 512 /* NEED_PATCH */)
    ])
  ]))
}
}

})